
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Logo, footerLinks } from '../constants.tsx';
import FastContactModal from './FastContactModal.tsx';
import { FraudNoticeBody, OPEN_FRAUD_NOTICE } from './FraudWarningModal.tsx';

const NEWSLETTER_ENDPOINT = 'https://tad-contact-worker.itsmybadsoul.workers.dev/';

type LinkTarget = { href: string; external?: boolean } | { action: 'contact' };

const linkTargets: Record<string, LinkTarget> = {
    footerMarket: { href: '/market/' },
    footerNews: { href: '/news/' },
    footerFeatures: { href: '/#features' },
    footerPricing: { href: '/#pricing' },
    footerDashboard: { href: 'https://dash.tad-ai.com', external: true },
    footerBotGeneral: { href: 'https://t.me/TAD_AI', external: true },
    footerBotVip: { href: 'https://t.me/TAD_VIP_bot', external: true },
    footerBotSupport: { action: 'contact' },
    footerAbout: { href: '/about/' },
    footerHelp: { href: '/help/' },
    footerStatus: { href: '/status/' },
    footerChangelog: { href: '/changelog/' },
    footerPrivacy: { href: '/privacy/' },
    footerRefund: { href: '/refund/' },
    footerTelegramChannel: { href: 'https://t.me/Tad_ai', external: true },
    footerWhatsApp: { href: 'https://wa.me/971541988034', external: true },
};

const Footer: React.FC = () => {
    const { t } = useTranslation();
    const [isContactModalOpen, setIsContactModalOpen] = useState(false);
    const [email, setEmail] = useState('');
    const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

    const handleNewsletter = async (e: React.FormEvent) => {
        e.preventDefault();
        if (newsletterStatus === 'submitting') return;
        const value = email.trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) {
            setNewsletterStatus('error');
            return;
        }
        setNewsletterStatus('submitting');
        try {
            const res = await fetch(NEWSLETTER_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: 'مشترك النشرة البريدية',
                    email: value,
                    phone: '',
                    message: 'طلب اشتراك في النشرة البريدية الأسبوعية من موقع tad-ai.com',
                    userId: 'newsletter-site',
                }),
            });
            if (!res.ok) throw new Error('newsletter request failed');
            setNewsletterStatus('success');
            setEmail('');
        } catch {
            setNewsletterStatus('error');
        }
    };

    return (
        <footer className="relative z-10 pb-16 lg:pb-20 xl:pb-24 pt-16 sm:pt-20 xl:pt-24">
            <div className="max-w-6xl mx-auto px-4 sm:px-8 xl:px-0 relative">
                <div className="w-full h-[1px] footer-divider-gradient absolute top-0 left-0"></div>

                {/* Newsletter */}
                <div className="flex flex-wrap items-center justify-between gap-10 pb-16">
                    <div className="max-w-md w-full text-start">
                        <h3 className="font-semibold text-2xl text-white mb-2">{t('newsletterTitle')}</h3>
                        <p className="font-medium">{t('newsletterDescription')}</p>
                    </div>
                    <div className="max-w-lg w-full">
                        <form onSubmit={handleNewsletter} noValidate>
                            <div className="flex items-center gap-4">
                                <div className="w-full">
                                    <input
                                        id="email-newsletter"
                                        type="email"
                                        name="email"
                                        autoComplete="email"
                                        value={email}
                                        onChange={(e) => {
                                            setEmail(e.target.value);
                                            if (newsletterStatus !== 'idle') setNewsletterStatus('idle');
                                        }}
                                        placeholder={t('formEmailPlaceholder')}
                                        aria-label={t('formEmailPlaceholder')}
                                        className="rounded-lg border border-white/[0.12] bg-white/[0.05] focus:border-brand w-full py-3 px-6 outline-none"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={newsletterStatus === 'submitting'}
                                    className="button-border-gradient relative rounded-lg text-white text-sm flex items-center gap-1.5 py-3.5 px-7 shadow-lg hover:shadow-none disabled:opacity-60"
                                >
                                    {newsletterStatus === 'submitting' ? t('newsletterSubmitting') : t('subscribeButton')}
                                </button>
                            </div>
                            <p
                                role="status"
                                aria-live="polite"
                                className={`mt-3 text-sm ${newsletterStatus === 'error' ? 'text-rose-400' : newsletterStatus === 'success' ? 'text-emerald-400' : 'text-white/40'}`}
                            >
                                {newsletterStatus === 'success'
                                    ? t('newsletterSuccess')
                                    : newsletterStatus === 'error'
                                        ? t('newsletterError')
                                        : t('newsletterNote')}
                            </p>
                        </form>
                    </div>
                </div>

                {/* Main Footer */}
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-8 sm:gap-10 text-start">
                    <div className="col-span-2 lg:col-span-1">
                        <a className="mb-8 inline-block" href="/" aria-label="TAD-AI"><Logo /></a>
                        <p className="mb-8">{t('footerDescription')}</p>
                        <div className="flex items-center gap-5">
                            <a
                                href="https://t.me/Tad_ai"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Telegram"
                                className="text-white/60 hover:text-white ease-in duration-300"
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15.82-1.07 4.9-1.53 6.95-.1.4-.24.7-.38.8-.3.2-.6.3-.9.3-.4 0-.75-.2-1-.4-.6-.4-1.05-.75-1.55-1.1-.6-.4-.3-1 .15-1.55.1-.15.95-.85 1.75-1.55.1-.1.2-.25.2-.35 0-.1-.1-.15-.2-.15-.15 0-1.15.65-2.9 1.75-.3.2-.55.3-.75.3-.25 0-.75-.15-1.15-.3-.45-.15-.85-.25-.8-.55.05-.15.35-.3.8-.5 2.8-1.2 4.65-2 5.55-2.4 2.65-1.1 3.2-1.3 3.55-1.3.1 0 .25.02.35.1.1.08.12.18.13.25z"/>
                                </svg>
                            </a>
                            <a
                                href="https://wa.me/971541988034"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="WhatsApp"
                                className="text-white/60 hover:text-white ease-in duration-300"
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.554 4.118 1.528 5.845L.057 23.43a.75.75 0 00.918.917l5.62-1.472A11.955 11.955 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75a9.708 9.708 0 01-4.944-1.347l-.355-.21-3.676.963.981-3.585-.23-.37A9.711 9.711 0 012.25 12C2.25 6.615 6.615 2.25 12 2.25S21.75 6.615 21.75 12 17.385 21.75 12 21.75z"/>
                                </svg>
                            </a>
                        </div>
                    </div>

                    {Object.entries(footerLinks).map(([titleKey, linkKeys]) => (
                        <div key={titleKey}>
                            <h5 className="font-semibold text-white mb-5">{t(titleKey)}</h5>
                            <ul className="flex flex-col gap-3.5">
                                {linkKeys.map(linkKey => {
                                    const target = linkTargets[linkKey];
                                    if (!target) return null;
                                    const linkClass = 'font-medium ease-in duration-300 hover:text-white text-white/80';
                                    return (
                                        <li key={linkKey}>
                                            {'action' in target ? (
                                                <button
                                                    type="button"
                                                    onClick={() => setIsContactModalOpen(true)}
                                                    className={`${linkClass} text-start`}
                                                >
                                                    {t(linkKey)}
                                                </button>
                                            ) : (
                                                <a
                                                    href={target.href}
                                                    target={target.external ? '_blank' : undefined}
                                                    rel={target.external ? 'noopener noreferrer' : undefined}
                                                    className={linkClass}
                                                >
                                                    {t(linkKey)}
                                                </a>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    ))}
                </div>

                {/* Anti-impersonation warning */}
                <div id="fraud-notice" className="mt-10 p-5 sm:p-6 rounded-2xl bg-rose-500/[0.05] border border-rose-500/25 text-start">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                        <h4 className="font-bold text-white text-base flex items-center gap-2">
                            <span aria-hidden="true">🛡️</span>
                            تنبيه رسمي — تحذير من الانتحال والنصب
                        </h4>
                        <button
                            type="button"
                            onClick={() => window.dispatchEvent(new Event(OPEN_FRAUD_NOTICE))}
                            className="rounded-lg border border-amber-400/40 bg-amber-400/10 text-amber-200 hover:bg-amber-400/20 font-semibold text-xs px-4 py-2 transition-colors"
                        >
                            أعِد قراءة التنبيه
                        </button>
                    </div>
                    <FraudNoticeBody muted />
                </div>

                {/* Legal Financial Disclaimer */}
                <div className="mt-12 p-5 rounded-2xl bg-white/[0.02] border border-white/[0.08] text-xs text-white/50 leading-relaxed text-center sm:text-start">
                    <p className="font-bold text-white/70 mb-1.5 flex items-center gap-1.5">
                        <span>⚠️</span>
                        <span>{t('footerDisclaimerTitle')}</span>
                    </p>
                    <p>{t('footerDisclaimerText')}</p>
                </div>

                <div className="flex flex-col sm:flex-row justify-between items-center mt-8 border-t border-white/10 pt-8">
                    <p className="font-medium text-center text-sm text-white/50 mb-4 sm:mb-0">
                        {t('footerCopyright', { year: new Date().getFullYear() })}
                    </p>
                    
                    <div className="flex flex-wrap items-center justify-center gap-3">
                        {/* Windows App Download Button */}
                        <a
                            href="https://www.tad-ai.com/TadAI-Setup.exe"
                            target="_blank"
                            rel="noopener noreferrer"
                            download
                            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600/20 border border-blue-500/40 text-blue-300 hover:bg-blue-600/30 font-semibold transition-all text-sm h-[44px]"
                        >
                            <span>💻</span>
                            <span className="flex flex-col leading-none items-start justify-center">
                                <span className="text-[9px] text-blue-400 font-normal mb-0.5">Windows EXE</span>
                                <span className="text-[11px] font-bold">تطبيق الكمبيوتر</span>
                            </span>
                        </a>

                        {/* Android App Download Button */}
                        <a
                            href="https://www.tad-ai.com/TADAI-Instaler.apk"
                            target="_blank"
                            rel="noopener noreferrer"
                            download
                            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-600/30 font-semibold transition-all text-sm h-[44px]"
                        >
                            <span>📱</span>
                            <span className="flex flex-col leading-none items-start justify-center">
                                <span className="text-[9px] text-emerald-400 font-normal mb-0.5">Android APK</span>
                                <span className="text-[11px] font-bold">تطبيق الأندرويد</span>
                            </span>
                        </a>

                        {/* Sara AI App Download Button */}
                        <a
                            href="https://www.tad-ai.com/SARA-AI.apk"
                            target="_blank"
                            rel="noopener noreferrer"
                            download
                            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-600/20 border border-purple-500/40 text-purple-300 hover:bg-purple-600/30 font-semibold transition-all text-sm h-[44px]"
                        >
                            <span>🤖</span>
                            <span className="flex flex-col leading-none items-start justify-center">
                                <span className="text-[9px] text-purple-400 font-normal mb-0.5">Sara AI APK</span>
                                <span className="text-[11px] font-bold">تطبيق سارة الذكي</span>
                            </span>
                        </a>

                        {/* Telegram Button */}
                        <a
                            href="https://t.me/+971541988034"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#229ED9]/10 border border-[#229ED9]/30 text-[#229ED9] hover:bg-[#229ED9]/20 font-semibold transition-all text-sm shadow-[0_0_15px_rgba(34,158,217,0.1)] h-[44px]"
                            dir="ltr"
                        >
                            <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15.82-1.07 4.9-1.53 6.95-.1.4-.24.7-.38.8-.3.2-.6.3-.9.3-.4 0-.75-.2-1-.4-.6-.4-1.05-.75-1.55-1.1-.6-.4-.3-1 .15-1.55.1-.15.95-.85 1.75-1.55.1-.1.2-.25.2-.35 0-.1-.1-.15-.2-.15-.15 0-1.15.65-2.9 1.75-.3.2-.55.3-.75.3-.25 0-.75-.15-1.15-.3-.45-.15-.85-.25-.8-.55.05-.15.35-.3.8-.5 2.8-1.2 4.65-2 5.55-2.4 2.65-1.1 3.2-1.3 3.55-1.3.1 0 .25.02.35.1.1.08.12.18.13.25z"/>
                            </svg>
                            <span className="flex flex-col leading-none items-start justify-center">
                                <span className="text-[9px] text-[#229ED9]/80 font-normal mb-0.5">Telegram</span>
                                <span className="text-[11px] font-semibold tracking-wide">971-541988034</span>
                            </span>
                        </a>

                        {/* WhatsApp Button */}
                        <a
                            href="https://wa.me/971541988034"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#25D366]/10 border border-[#25D366]/30 text-[#25D366] hover:bg-[#25D366]/20 font-semibold transition-all text-sm shadow-[0_0_15px_rgba(37,211,102,0.1)] h-[44px]"
                            dir="ltr"
                        >
                            <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.554 4.118 1.528 5.845L.057 23.43a.75.75 0 00.918.917l5.62-1.472A11.955 11.955 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75a9.708 9.708 0 01-4.944-1.347l-.355-.21-3.676.963.981-3.585-.23-.37A9.711 9.711 0 012.25 12C2.25 6.615 6.615 2.25 12 2.25S21.75 6.615 21.75 12 17.385 21.75 12 21.75z"/>
                            </svg>
                            <span className="flex flex-col leading-none items-start justify-center">
                                <span className="text-[9px] text-[#25D366]/80 font-normal mb-0.5">WhatsApp</span>
                                <span className="text-[11px] font-semibold tracking-wide">971-541988034</span>
                            </span>
                        </a>

                        {/* Fast Contact Button */}
                        <button 
                            onClick={() => setIsContactModalOpen(true)}
                            className="button-border-gradient relative rounded-lg text-white text-sm flex items-center gap-2 py-2.5 px-6 ease-in duration-300 hover:opacity-80 h-[44px]"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>
                            مراسلة سريعة
                        </button>
                    </div>
                </div>
                
                <FastContactModal 
                    isOpen={isContactModalOpen} 
                    onClose={() => setIsContactModalOpen(false)} 
                />
            </div>
        </footer>
    );
};

export default Footer;
